import styles from './VerifyPhoneNumber.module.scss';

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Trans, useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';

import { Icon } from 'components/Icon';
import { Modal } from 'components/Modal';
import { Button, ButtonVariant } from '@clover/web-lib-consumer-ui';
import { CloseButton } from 'components/CloseButton';
import { FieldError } from 'components/FieldError';

import { useCustomer } from 'hooks/context/customer/customer';
import { useCloseNavigate } from 'hooks/useCloseNavigate/useCloseNavigate';
import { useReCaptcha } from 'hooks/context/ReCaptcha';
import { useSmartNavigate } from 'hooks/useSmartNavigate';

import { parsePhoneNumber } from 'util/Formatting';
import { RouteFC } from 'util/routeTypes';
import { generateId } from 'util/generateId';
import { parseAuthErrorMessage } from 'util/ErrorHandling/errorHandling.util';

import { VERIFY_PHONE_NUMBER } from './const';
import { getRedirectPathForDeliveryError, onClosePath } from '../Authentication.util';
import { useDeliveryValidation } from 'hooks/useDeliveryValidation';

export enum VerifyPhoneErrors {
  SMS_CODE_INVALID = 'SMS_CODE_INVALID',
  SMS_MAX_CODES_EXCEEDED = 'SMS_MAX_CODES_EXCEEDED',
  SMS_CONFIRMATION_ENTRY_INVALID = 'SMS_CONFIRMATION_ENTRY_INVALID',
  UNKNOWN = 'UNKNOWN'
}

type VerifyPhoneNumberLocationStateProps = {
  redirectPath: string;
  basePath: string;
  phoneNumber: string;
  isDeliveryFlow: boolean;
}

export const VerifyPhoneNumber: RouteFC<{ children: never, props: RouteComponentProps }> = memo((props) => {
  const state = props.location?.state;
  const { redirectPath, basePath, phoneNumber, isDeliveryFlow } = state as VerifyPhoneNumberLocationStateProps || {};

  // Hooks
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const { sendPhoneCode, confirmPhoneCode } = useCustomer();
  const { attemptReCaptcha } = useReCaptcha();
  const onClose = useCloseNavigate(onClosePath(redirectPath));
  const errorTextId = useRef(generateId()).current;
  const { getLatestAddressAndValidate } = useDeliveryValidation();

  // States
  const [code, setCode] = useState('');
  const [showErrorState, setShowErrorState] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Callbacks
  const clearState = useCallback(() => {
    setCode('');
    setErrorMessage('');
    setShowErrorState(false);
  }, []);

  const sendCode = async () => {
    const reCaptchaToken = await attemptReCaptcha();
    try {
      await sendPhoneCode(parsePhoneNumber(phoneNumber).number, reCaptchaToken);
      clearState();
    } catch (error) {
      setErrorMessage(parseAuthErrorMessage(error));
      setShowErrorState(true);
    }
  };

  const confirmCode = async () => {
    try {
      await confirmPhoneCode(parsePhoneNumber(phoneNumber).number, code);

      getLatestAddressAndValidate(isDeliveryFlow).then(() => {
        navigate(basePath);
      }).catch((e) => {
        if (isDeliveryFlow) {
          navigate(getRedirectPathForDeliveryError(redirectPath, e.message), { state: {
            mainAddress: e.address
          } });
        } else {
          navigate(redirectPath);
        }
      });

    } catch (error) {
      setErrorMessage(parseAuthErrorMessage(error));
      setShowErrorState(true);
    }
  };

  // If customer is linked here without a phonenumber in state return to login
  useEffect(() => {
    if (!phoneNumber) navigate('~login/info', { state: { redirectPath } });
  }, [navigate, phoneNumber, redirectPath]);

  // Once all 6 digits are entered automatically call confirm endpoint
  useEffect(() => {
    if (code.length === 6) {
      confirmCode();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <Modal
      aria={{ labelledby: VERIFY_PHONE_NUMBER.id }}
      className={styles.modal}
      isOpen={true}
      onClose={onClose}
      testId={VERIFY_PHONE_NUMBER.root}
    >
      <header className={styles.modalHeader}>
        <Icon className={styles.leftArrowIcon} name="leftArrow" size={18} onClick={() => navigate('.info', { state: { redirectPath } })}></Icon>
        {onClose && <CloseButton className={styles.closeButton} onClick={onClose} />}
      </header>

      <section className={styles.body}>
        <Icon className={styles.icon} name={'messageBubble'} height={48} width={60}></Icon>
        <p className={styles.title} id={VERIFY_PHONE_NUMBER.id}>{t('phoneValidation.title')}</p>
        {phoneNumber && <div className={styles.subtitle}>
          <Trans i18nKey="phoneValidation.subtitle">
            A 6-digit code was just sent to <span className={styles.bold}>{{ phoneNumber: parsePhoneNumber(phoneNumber).format('NATIONAL') }}</span>. Please enter it below to keep your account information safe.
          </Trans>
        </div> }
        <div className={styles.inputFieldWrapper} data-testid={VERIFY_PHONE_NUMBER.otpInput}>
          {errorMessage !== VerifyPhoneErrors.SMS_MAX_CODES_EXCEEDED && <OtpInput
            numInputs={6}
            value={code}
            onChange={setCode}
            containerStyle={styles.containerStyle}
            inputStyle={styles.inputStyle}
            focusStyle={styles.focusStyle}
            errorStyle={styles.errorStyle}
            isInputNum={true}
            shouldAutoFocus={true}
            hasErrored={showErrorState}
            data-testid={VERIFY_PHONE_NUMBER.otpInput}
          />}
        {showErrorState && (
          <FieldError id={errorTextId} data-testid={errorMessage} className={styles.errorText}>{t(`authErrorMessage.${errorMessage}`, { contact: parsePhoneNumber(phoneNumber).format('NATIONAL') }) }</FieldError>
        )}
        </div>
      </section>
      {errorMessage !== VerifyPhoneErrors.SMS_MAX_CODES_EXCEEDED && <footer className={styles.footer}>
        <Button variant={ButtonVariant.INVERTED} className={styles.button} onClick={sendCode} data-testid={VERIFY_PHONE_NUMBER.sendNewCode}>{t('phoneValidation.sendNewCode')}</Button>
      </footer>}
      {errorMessage === VerifyPhoneErrors.SMS_MAX_CODES_EXCEEDED && <footer className={styles.footer}>
        <Button className={styles.button} onClick={() => navigate('.info', { state: { redirectPath, defaultToEmail: true } })} data-testid={VERIFY_PHONE_NUMBER.loginWithEmail}>{t('authForm.loginWithEmail')}</Button>
        <Button variant={ButtonVariant.INVERTED} className={styles.button} onClick={() => navigate(redirectPath)} data-testid={VERIFY_PHONE_NUMBER.checkout}>{t('authForm.checkoutAsGuest')}</Button>
      </footer>}
    </Modal>
  );
});
